import type {ISketchObject} from '../../mesh/sketch/ISketchObject';
import type {SketchPointObject} from '../../mesh/sketch/SketchPointObject';
import type {SketchGroup} from '../../mesh/sketch/SketchGroup';
import type {SketchRelationDefinition} from '../solver/constraint/relationDefinition/SketchRelationDefinition';
import {detachConstraintFromAllTargets} from '../solver/constraint/utils/detachConstraintFromAllTargets';
import type {SketchTrimCurveObject} from './SketchTrimTypes';

//------ 强力裁剪约束清理 开始------
/**
 * 强力裁剪约束清理结果。
 */
export interface SketchTrimConstraintCleanupResult {
    /** 被移除的约束 ID 列表。 */
    removedConstraintIds: number[];
    /** 参与本次清理判定的对象 UUID 列表。 */
    seedObjectIds: string[];
}

/**
 * 强力裁剪约束清理服务。
 *
 * 设计说明：
 * 1. 裁剪会删除、拆分或替换草图曲线端点，原几何关系继续指向旧对象会形成悬空关系；
 * 2. 历史快照复制草图组时，多目标关系要求所有 target 都有副本，悬空关系会导致复制失败；
 * 3. 本服务只负责在几何变更前解除被裁剪曲线及其专属端点参与的约束，不参与裁剪几何计算。
 */
export class SketchTrimConstraintCleanupService {
    /**
     * 清理被裁剪曲线相关的约束。
     * @param curve 待裁剪曲线。
     * @param sketchGroup 当前草图组；提供时同步回收失去最后关系的外部引用目标。
     * @returns 清理结果。
     */
    public cleanupCurveConstraints(
        curve: SketchTrimCurveObject,
        sketchGroup: SketchGroup | null = null,
    ): SketchTrimConstraintCleanupResult {
        const seedObjects = this.collectSeedObjects(curve);
        const constraints = this.collectRemovableConstraints(curve, seedObjects);

        constraints.forEach((constraint) => {
            detachConstraintFromAllTargets(constraint);
        });

        sketchGroup?.getSketchReferenceTargets().forEach((referenceTarget) => {
            if (referenceTarget.constraints.getRelations().length > 0) {
                return;
            }
            sketchGroup.removeSketchReferenceTarget(referenceTarget);
            referenceTarget.dispose();
        });

        return {
            removedConstraintIds: constraints.map((constraint) => constraint.id),
            seedObjectIds: seedObjects.map((object) => object.uuid),
        };
    }

    /**
     * 收集裁剪曲线和相关点作为清理种子。
     * @param curve 待裁剪曲线。
     * @returns 清理种子对象列表。
     */
    private collectSeedObjects(curve: SketchTrimCurveObject): ISketchObject[] {
        const seedMap = new Map<string, ISketchObject>();
        seedMap.set(curve.uuid, curve);

        // 轻量测试替身或历史恢复中的旧对象可能只具备曲线 ID；真实草图对象会提供相关点快照。
        if (typeof curve.getRelatedSketchPointsSnapshot !== 'function') {
            return Array.from(seedMap.values());
        }

        curve.getRelatedSketchPointsSnapshot().forEach((point) => {
            seedMap.set(point.uuid, point);
        });
        return Array.from(seedMap.values());
    }

    /**
     * 收集需要移除的约束。
     * @param curve 待裁剪曲线。
     * @param seedObjects 清理种子对象列表。
     * @returns 待移除约束列表。
     */
    private collectRemovableConstraints(
        curve: SketchTrimCurveObject,
        seedObjects: readonly ISketchObject[],
    ): SketchRelationDefinition<ISketchObject>[] {
        const constraintMap = new Map<number, SketchRelationDefinition<ISketchObject>>();
        seedObjects.forEach((seedObject) => {
            if (!seedObject.constraints || typeof seedObject.constraints.getRelations !== 'function') {
                return;
            }

            const constraints = seedObject.constraints.getRelations() as SketchRelationDefinition<ISketchObject>[];
            constraints.forEach((constraint) => {
                if (!this.shouldRemoveConstraintForSeed(curve, seedObject, constraint)) {
                    return;
                }
                constraintMap.set(constraint.id, constraint);
            });
        });
        return Array.from(constraintMap.values());
    }

    /**
     * 判断某条约束是否应由当前种子对象触发移除。
     * @param curve 待裁剪曲线。
     * @param seedObject 当前种子对象。
     * @param constraint 待判断约束。
     * @returns 应移除返回 true。
     */
    private shouldRemoveConstraintForSeed(
        curve: SketchTrimCurveObject,
        seedObject: ISketchObject,
        constraint: SketchRelationDefinition<ISketchObject>,
    ): boolean {
        if (seedObject.uuid === curve.uuid) {
            return true;
        }

        const point = seedObject as SketchPointObject;
        if (!this.isPointOwnedByTrimCurve(point, curve)) {
            return this.hasConstraintTarget(constraint, curve);
        }

        return true;
    }

    /**
     * 判断点是否专属于当前裁剪曲线。
     *
     * 说明：
     * 1. 中点构造线端点通常只连接自身构造线，但会通过 `Midpoint` 关系引用外框边；
     * 2. 这类点在裁剪构造线时必须解除全部关系，否则外框边仍会保存指向旧点的关系；
     * 3. 共享矩形角点连接了外框边和构造线，不能清掉它的其它关系，只清掉直接包含被裁剪曲线的关系。
     *
     * @param point 待判断点。
     * @param curve 待裁剪曲线。
     * @returns 专属于当前曲线返回 true。
     */
    private isPointOwnedByTrimCurve(point: SketchPointObject, curve: SketchTrimCurveObject): boolean {
        if (typeof point.getConnectedEdgesSnapshot !== 'function') {
            return false;
        }

        const connectedEdges = point.getConnectedEdgesSnapshot();
        if (connectedEdges.length <= 0) {
            return true;
        }

        return connectedEdges.every((edge) => edge.uuid === curve.uuid);
    }

    /**
     * 判断约束是否直接包含指定目标对象。
     * @param constraint 待判断约束。
     * @param target 目标对象。
     * @returns 包含返回 true。
     */
    private hasConstraintTarget(
        constraint: SketchRelationDefinition<ISketchObject>,
        target: ISketchObject,
    ): boolean {
        return constraint.getTargetsSnapshot().some((constraintTarget) => {
            return constraintTarget.uuid === target.uuid;
        });
    }

}
//------ 强力裁剪约束清理 结束------
